import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { loadEnvConfig, readDepartmentAgenciesGridPageSize, readLandingAgenciesGridId, readLandingDepartmentsGridId, readLandingPermitsCatalogGridId, readLandingPermitsCatalogPageSize, } from '../services/envConfig';
import { fetchLandingAgenciesFromGrid, fetchLandingDepartmentsFromGrid, fetchLandingPermitsCatalogFromGrid, } from '../services/landingDepartmentsGrid';
const initialState = {
    departmentsRaw: [],
    agencies: [],
    permitsCatalog: [],
    status: 'idle',
    departmentsLoadError: false,
    agenciesLoadError: false,
    permitsCatalogLoadError: false,
};
let permitsCatalogFetchInFlight = false;
export const fetchLandingGrids = createAsyncThunk('landingGrids/fetchLandingGrids', async (_, thunkAPI) => {
    const env = await loadEnvConfig();
    if (!env) {
        return {
            departments: [],
            agencies: [],
            permitsCatalog: [],
            departmentsLoadError: false,
            agenciesLoadError: false,
            permitsCatalogLoadError: false,
        };
    }
    const sidebar = typeof env.Sidebar === 'string' ? env.Sidebar.trim() : '';
    const deptGridId = readLandingDepartmentsGridId(env);
    const agencyGridId = readLandingAgenciesGridId(env);
    const permitsCatalogGridId = readLandingPermitsCatalogGridId(env);
    const agencyPageSize = readDepartmentAgenciesGridPageSize(env);
    const permitsCatalogPageSize = readLandingPermitsCatalogPageSize(env);
    const deptPromise = sidebar && deptGridId
        ? fetchLandingDepartmentsFromGrid(sidebar, deptGridId).then((list) => ({ ok: true, list }), () => ({ ok: false, list: [] }))
        : Promise.resolve({ ok: true, list: [] });
    const agencyPromise = sidebar && agencyGridId
        ? fetchLandingAgenciesFromGrid(sidebar, agencyGridId, agencyPageSize).then((list) => ({ ok: true, list }), () => ({ ok: false, list: [] }))
        : Promise.resolve({ ok: true, list: [] });
    const processFlowDetailsTemplate = (typeof env.ProcessFlowDetailsUrl === 'string' && env.ProcessFlowDetailsUrl.trim()
        ? env.ProcessFlowDetailsUrl.trim()
        : typeof env.processFlowDetailsUrl === 'string' && env.processFlowDetailsUrl.trim()
            ? env.processFlowDetailsUrl.trim()
            : typeof env.PermitViewDetailsUrl === 'string' && env.PermitViewDetailsUrl.trim()
                ? env.PermitViewDetailsUrl.trim()
                : '') || '';
    // Wait for departments + agencies only so initial UI can render.
    const [deptResult, agencyResult] = await Promise.all([deptPromise, agencyPromise]);
    // Start permits catalog fetch in background (do not await here)
    if (sidebar && permitsCatalogGridId) {
        try {
            const state = thunkAPI.getState();
            const existing = state?.landingGrids?.permitsCatalog;
            const hasExisting = Array.isArray(existing) && existing.length > 0;
            if (!hasExisting && !permitsCatalogFetchInFlight) {
                permitsCatalogFetchInFlight = true;
                void thunkAPI
                    .dispatch(fetchLandingPermitsCatalog())
                    .then(() => {
                    permitsCatalogFetchInFlight = false;
                })
                    .catch(() => {
                    permitsCatalogFetchInFlight = false;
                });
            }
        }
        catch {
            // ignore state read errors and still attempt dispatch
            void thunkAPI.dispatch(fetchLandingPermitsCatalog());
        }
    }
    return {
        departments: deptResult.list,
        departmentsLoadError: !deptResult.ok,
        agencies: agencyResult.list,
        agenciesLoadError: !agencyResult.ok,
        permitsCatalog: [],
        permitsCatalogLoadError: false,
    };
}, {
    /**
     * Dedupe concurrent/repeat dispatches (e.g. React StrictMode double-invokes the mount effect, or
     * multiple pages dispatch on load) so the grid URLs are fetched once. Reads live state, unlike the
     * component's captured-status guard.
     */
    condition: (_arg, { getState }) => {
        const { status } = getState().landingGrids;
        return status === 'idle' || status === 'failed';
    },
});
export const fetchLandingPermitsCatalog = createAsyncThunk('landingGrids/fetchLandingPermitsCatalog', async () => {
    const env = await loadEnvConfig();
    if (!env)
        return { permitsCatalog: [], permitsCatalogLoadError: false };
    const sidebar = typeof env.Sidebar === 'string' ? env.Sidebar.trim() : '';
    const permitsCatalogGridId = readLandingPermitsCatalogGridId(env);
    const permitsCatalogPageSize = readLandingPermitsCatalogPageSize(env);
    const processFlowDetailsTemplate = (typeof env.ProcessFlowDetailsUrl === 'string' && env.ProcessFlowDetailsUrl.trim()
        ? env.ProcessFlowDetailsUrl.trim()
        : typeof env.processFlowDetailsUrl === 'string' && env.processFlowDetailsUrl.trim()
            ? env.processFlowDetailsUrl.trim()
            : typeof env.PermitViewDetailsUrl === 'string' && env.PermitViewDetailsUrl.trim()
                ? env.PermitViewDetailsUrl.trim()
                : '') || '';
    if (!sidebar || !permitsCatalogGridId)
        return { permitsCatalog: [], permitsCatalogLoadError: false };
    try {
        const list = await fetchLandingPermitsCatalogFromGrid(sidebar, permitsCatalogGridId, permitsCatalogPageSize, processFlowDetailsTemplate || undefined);
        return { permitsCatalog: list, permitsCatalogLoadError: false };
    }
    catch {
        return { permitsCatalog: [], permitsCatalogLoadError: true };
    }
});
const landingGridsSlice = createSlice({
    name: 'landingGrids',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchLandingGrids.pending, (state) => {
            state.status = 'loading';
            state.departmentsRaw = [];
            state.agencies = [];
            state.permitsCatalog = [];
            state.departmentsLoadError = false;
            state.agenciesLoadError = false;
            state.permitsCatalogLoadError = false;
        })
            .addCase(fetchLandingGrids.fulfilled, (state, action) => {
            console.log('[DEBUG] Redux fulfilled - departments count:', action.payload.departments.length);
            console.log('[DEBUG] Redux fulfilled - agencies count:', action.payload.agencies.length);
            console.log('[DEBUG] Redux fulfilled - permits catalog count:', action.payload.permitsCatalog.length);
            state.status = 'succeeded';
            state.departmentsRaw = action.payload.departments;
            state.agencies = action.payload.agencies;
            state.permitsCatalog = action.payload.permitsCatalog;
            state.departmentsLoadError = action.payload.departmentsLoadError;
            state.agenciesLoadError = action.payload.agenciesLoadError;
            state.permitsCatalogLoadError = action.payload.permitsCatalogLoadError;
        })
            .addCase(fetchLandingGrids.rejected, (state) => {
            state.status = 'failed';
            state.departmentsRaw = [];
            state.agencies = [];
            state.permitsCatalog = [];
            state.departmentsLoadError = true;
            state.agenciesLoadError = true;
            state.permitsCatalogLoadError = true;
        });
        builder
            .addCase(fetchLandingPermitsCatalog.fulfilled, (state, action) => {
            state.permitsCatalog = action.payload.permitsCatalog;
            state.permitsCatalogLoadError = action.payload.permitsCatalogLoadError;
        })
            .addCase(fetchLandingPermitsCatalog.rejected, (state) => {
            state.permitsCatalogLoadError = true;
        });
    },
});
export default landingGridsSlice.reducer;
